/**
 * Reusable TypeBox schema fragments.
 *
 * Composed into every entity schema. Centralised here so that the
 * universal conventions (UUID format, ISO date strings, row_version for
 * optimistic concurrency, soft delete) are defined exactly once.
 */

import { Type, type Static } from '@sinclair/typebox';

// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------

/** Stable identifier — UUID v4/v7. We accept any UUID; Drizzle generates v4. */
export const UuidSchema = Type.String({ format: 'uuid' });
export type Uuid = Static<typeof UuidSchema>;

/** ISO-8601 date-time string. Storage in DB is `timestamptz`. */
export const IsoDateTimeSchema = Type.String({ format: 'date-time' });
export type IsoDateTime = Static<typeof IsoDateTimeSchema>;

/** ISO-8601 date-only string (YYYY-MM-DD). Used for business dates without time. */
export const IsoDateSchema = Type.String({ format: 'date' });
export type IsoDate = Static<typeof IsoDateSchema>;

/** Currency amount in paisa (integer). */
export const PaisaSchema = Type.Integer({ minimum: 0 });
export type Paisa = Static<typeof PaisaSchema>;

/**
 * Row version for optimistic concurrency.
 *
 * Sent back on read, must be echoed on update. Server returns 409 if the
 * stored version is greater than the supplied one.
 */
export const RowVersionSchema = Type.Integer({ minimum: 0 });
export type RowVersion = Static<typeof RowVersionSchema>;

// ---------------------------------------------------------------------------
// Composite fragments
// ---------------------------------------------------------------------------

/** Fields every persistent entity carries. */
export const TimestampFields = {
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
} as const;

/** Soft-delete fields — only present on entities the audit log can reference. */
export const SoftDeleteFields = {
  deletedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  deletedByUserId: Type.Union([UuidSchema, Type.Null()]),
} as const;

/** Row version field — present on every editable entity. */
export const RowVersionField = {
  rowVersion: RowVersionSchema,
} as const;

// ---------------------------------------------------------------------------
// Pagination & list responses
// ---------------------------------------------------------------------------

export const PaginationParamsSchema = Type.Object({
  page: Type.Optional(Type.Integer({ minimum: 1, default: 1 })),
  pageSize: Type.Optional(Type.Integer({ minimum: 1, maximum: 200, default: 50 })),
});
export type PaginationParams = Static<typeof PaginationParamsSchema>;

export function PaginatedResponseSchema<T extends ReturnType<typeof Type.Object>>(itemSchema: T) {
  return Type.Object({
    items: Type.Array(itemSchema),
    total: Type.Integer({ minimum: 0 }),
    page: Type.Integer({ minimum: 1 }),
    pageSize: Type.Integer({ minimum: 1 }),
  });
}

// ---------------------------------------------------------------------------
// Error envelope
// ---------------------------------------------------------------------------

export const ApiErrorSchema = Type.Object({
  statusCode: Type.Integer(),
  error: Type.String(),
  message: Type.String(),
  /** Optional field-level validation errors */
  details: Type.Optional(
    Type.Array(
      Type.Object({
        path: Type.String(),
        message: Type.String(),
      }),
    ),
  ),
});
export type ApiError = Static<typeof ApiErrorSchema>;
